/**
 * Settings — the unified admin settings page (under Amministrazione). A small
 * left sub-nav splits the categories: Odino agent, Voice, and Security & network
 * policy (ADR-0027). Each category is a self-contained panel with its own load +
 * save. The active category rides in `?section=` so refresh / deep-link survive;
 * only categories the operator can access are shown.
 */
import { useQuery } from "@tanstack/react-query";
import {
  Bot,
  Building2,
  CreditCard,
  KeyRound,
  LifeBuoy,
  Mic,
  type LucideIcon,
  ShieldCheck,
} from "lucide-react";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";

import { FeatureGate } from "@/components/billing/FeatureGate";
import { BillingSection } from "@/components/settings/BillingSection";
import { LicenseSection } from "@/components/settings/LicenseSection";
import { OdinoSection } from "@/components/settings/OdinoSection";
import { PartnerAccessSection } from "@/components/settings/PartnerAccessSection";
import { PolicySettings } from "@/components/settings/PolicySettings";
import { TenantProfileSection } from "@/components/settings/TenantProfileSection";
import { VoiceSection } from "@/components/settings/VoiceSection";
import { EmptyState, PageContainer, PageContent, PageHeader } from "@/components/ui/page";
import { licenseApi } from "@/lib/api-license";
import { cn } from "@/lib/cn";
import { usePermissions } from "@/lib/permissions";

interface SettingsSection {
  key: string;
  labelKey: string;
  fallback: string;
  icon: LucideIcon;
  resource: string;
  action: string;
  render: () => React.ReactNode;
}

const SECTIONS: SettingsSection[] = [
  {
    key: "odino",
    labelKey: "settings.nav.odino",
    fallback: "Odino",
    icon: Bot,
    resource: "odino",
    action: "use",
    render: () => (
      <FeatureGate feature="ai_plane">
        <OdinoSection />
      </FeatureGate>
    ),
  },
  {
    key: "voice",
    labelKey: "settings.nav.voice",
    fallback: "Voce",
    icon: Mic,
    resource: "odino",
    action: "use",
    render: () => (
      <FeatureGate feature="ai_plane">
        <VoiceSection />
      </FeatureGate>
    ),
  },
  {
    key: "profile",
    labelKey: "settings.nav.profile",
    fallback: "Profilo azienda",
    icon: Building2,
    resource: "settings",
    action: "read",
    render: () => <TenantProfileSection />,
  },
  {
    key: "billing",
    labelKey: "settings.nav.billing",
    fallback: "Abbonamento",
    icon: CreditCard,
    resource: "settings",
    action: "read",
    render: () => <BillingSection />,
  },
  {
    key: "security",
    labelKey: "settings.nav.security",
    fallback: "Sicurezza & rete",
    icon: ShieldCheck,
    resource: "settings",
    action: "read",
    render: () => <PolicySettings />,
  },
  {
    key: "partner",
    labelKey: "settings.nav.partner",
    fallback: "Accesso partner",
    icon: LifeBuoy,
    resource: "settings",
    action: "read",
    render: () => <PartnerAccessSection />,
  },
  {
    key: "license",
    labelKey: "settings.nav.license",
    fallback: "Licenza",
    icon: KeyRound,
    resource: "settings",
    action: "read",
    render: () => <LicenseSection />,
  },
];

export function SettingsPage() {
  const { t } = useTranslation();
  const { can } = usePermissions();
  const [params, setParams] = useSearchParams();

  // The Licenza section is the ON-PREM activation surface (ADR-0073): on the SaaS /
  // in dev the backend reports `enforced: false` and the panel could only ever say
  // "enforcement non attivo" — noise, so the tab is hidden entirely. Shown only on a
  // licensed on-prem install (enforced), where it is the real activation/heartbeat UI.
  const licenseQ = useQuery({
    queryKey: ["license", "status"],
    queryFn: licenseApi.status,
    enabled: can("settings", "read"),
    staleTime: 5 * 60_000,
  });
  // `enforced` is the one honest discriminator between the two deployments: only the
  // `tray-onprem` image bakes it, and it is exactly what makes the licence real. So it
  // gates the pair — the two panels are mutually exclusive, and an on-prem install used
  // to show the WRONG one of them: **Abbonamento** is the SaaS self-service surface
  // (plans, Stripe change-plan, the organisation's workspaces), which on-prem has no
  // business rendering — there is no Stripe there, billing is a contract with the vendor.
  const licenseEnforced = licenseQ.data?.enforced === true;

  const visible = SECTIONS.filter((s) => {
    if (!can(s.resource, s.action)) return false;
    if (s.key === "license") return licenseEnforced;
    if (s.key === "billing") return !licenseEnforced;
    return true;
  });
  const requested = params.get("section");
  const active = visible.find((s) => s.key === requested) ?? visible[0];

  const select = (key: string) => {
    const next = new URLSearchParams(params);
    next.set("section", key);
    setParams(next, { replace: true });
  };

  return (
    <PageContainer>
      <PageHeader
        title={t("settings.title", "Impostazioni")}
        description={t(
          "settings.subtitle",
          "Configurazione dell'istanza: agente Odino, voce e policy di sicurezza/rete.",
        )}
      />
      <PageContent>
        {!active ? (
          <EmptyState
            icon={ShieldCheck}
            title={t("settings.noAccess", "Nessuna impostazione accessibile")}
            description={t(
              "settings.noAccessHint",
              "Non hai i permessi per nessuna categoria di impostazioni.",
            )}
          />
        ) : (
          <div className="flex flex-col gap-6 md:flex-row">
            {/* Left sub-nav */}
            <nav className="shrink-0 md:w-52">
              <ul className="flex gap-1 overflow-x-auto md:flex-col md:gap-0.5">
                {visible.map((s) => {
                  const Icon = s.icon;
                  const isActive = s.key === active.key;
                  return (
                    <li key={s.key}>
                      <button
                        type="button"
                        onClick={() => select(s.key)}
                        className={cn(
                          "flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-sm transition-colors",
                          isActive
                            ? "bg-elevated font-medium text-foreground"
                            : "text-muted-foreground hover:bg-elevated/50 hover:text-foreground",
                        )}
                      >
                        <Icon className="h-4 w-4 shrink-0" strokeWidth={1.5} />
                        <span className="truncate">{t(s.labelKey, s.fallback)}</span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </nav>

            {/* Active panel */}
            <div className="min-w-0 flex-1">{active.render()}</div>
          </div>
        )}
      </PageContent>
    </PageContainer>
  );
}
